// Which kiosk this browser shows on a Plant board tab: the shop kiosk on the
// Inventory tab (ShopKiosk.tsx), the water spider's on the Water Spider tab
// (plant/WaterSpiderKiosk.tsx). Kept per browser, one key per kiosk, so the
// touchscreen at the inventory station and the vertical screen by the casting
// lines each stay on their kiosk through every MOS update reload (main.tsx
// keeps these keys when it clears storage), and an office browser that once
// peeked at one goes back to the board on Exit.

import { saveLastLocation } from '../../utils/lastLocation';

export const KIOSK_MODE_KEY = 'mos:inventory-kiosk';
export const WATER_SPIDER_KIOSK_MODE_KEY = 'mos:water-spider-kiosk';

/** The kiosks a browser can be parked on, each the floor screen of one Plant board tab. */
export type KioskName = 'inventory' | 'water-spider';
export const KIOSKS: Record<KioskName, { key: string; tab: string }> = {
  inventory: { key: KIOSK_MODE_KEY, tab: 'inventory' },
  'water-spider': { key: WATER_SPIDER_KIOSK_MODE_KEY, tab: 'water-spider' },
};
export const KIOSK_MODE_KEYS = Object.values(KIOSKS).map(k => k.key);

/**
 * The address a dedicated kiosk opens: `/?kiosk=inventory` (the shop PC's
 * Chrome shortcut, like the tablets' Knox profile pointing at MOS) or
 * `/?kiosk=water-spider` (the vertical screen by the casting lines). It puts
 * the browser on that Plant board tab with the kiosk on, before the app
 * starts, so the machine lands on the kiosk after every restart and every
 * reload, whoever pressed Exit last. The address keeps the word, so a reload
 * re-applies it; an ordinary address changes nothing.
 */
export const KIOSK_LAUNCH = 'inventory';

/** Which kiosk an address launches, or none. */
export function kioskLaunchOf(href: string): KioskName | null {
  let wanted: string | null = null;
  try {
    wanted = new URL(href).searchParams.get('kiosk');
  } catch {
    return null;
  }
  return wanted && wanted in KIOSKS ? (wanted as KioskName) : null;
}

export function applyKioskLaunch(href: string): boolean {
  const name = kioskLaunchOf(href);
  if (!name) return false;
  writeKioskMode(true, name);
  saveLastLocation({ view: 'plant-workbench', workbenchTab: KIOSKS[name].tab });
  return true;
}

export function readKioskMode(name: KioskName = 'inventory'): boolean {
  try {
    return localStorage.getItem(KIOSKS[name].key) === '1';
  } catch {
    return false;
  }
}

export function writeKioskMode(on: boolean, name: KioskName = 'inventory'): void {
  try {
    if (on) localStorage.setItem(KIOSKS[name].key, '1');
    else localStorage.removeItem(KIOSKS[name].key);
  } catch { /* private mode: the kiosk still opens, it just isn't remembered */ }
}
